<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import { apiPost } from '~/lib/http'
import { usePageContext } from '~/composables/usePageContext'
import { useScreen } from '~/composables/useScreen'
import type { Student2Context } from '~/types/student2'
import { useNavigation } from './useNavigation'

const context = usePageContext<Student2Context>()
const { isMobile } = useScreen()
const { getLink, goto } = useNavigation()
const isOpen = ref<boolean>(false)
const { notify } = useNotification()

const skippedLesson = computed(() => context.student.skipped_lessons?.at(-1))
const lessonStorageKey = computed(() => {
  const lesson = skippedLesson.value
  if (!lesson) return null
  return lesson.unit_id.toString() + '|' + lesson.lesson_name
})

const videoTeacherGame = computed(() => {
  return context.games.find(g => g.name === 'videoteacher')
})
const modalTitle = computed(() => {
  let result = ''

  if (context.student.gender === 'f') result += 'Ты пропустила урок '
  else result += 'Ты пропустил урок '

  result += skippedLesson.value?.lesson_name

  return result
})

onMounted(() => {
  document.addEventListener('DOMContentLoaded', () => {
    if (!skippedLesson.value) return
    if (!videoTeacherGame.value) return
    if (localStorage.studentHideSkippedLesson === lessonStorageKey.value) return
    if (sessionStorage.studentHideSkippedLesson === lessonStorageKey.value) return

    isOpen.value = true
  })
})

async function openVideoTeacher() {
  if (!videoTeacherGame.value) return
  if (!(await dismissAndRemember())) return
  sessionStorage.studentHideSkippedLesson = lessonStorageKey.value
  goto(
    getLink({
      hash: `game=${videoTeacherGame.value.id}`,
      unitId: skippedLesson.value?.unit_id
    })
  )
}

async function dismissAndRemember() {
  if (!skippedLesson.value) return false
  try {
    const { failed, message } = await apiPost('/student/request_dismiss_videoteacher_reminder', {
      student_id: context.student.id,
      binding_id: skippedLesson.value.binding_id
    })
    if (failed) throw message
  } catch (error) {
    let title = 'Не удалось отключить напоминание. Попробуйте ещё раз.'
    if (typeof error === 'string') title = error
    notify({ type: 'error', title })
    return false
  }
  isOpen.value = false
  return true
}

function dismiss() {
  sessionStorage.studentHideSkippedLesson = lessonStorageKey.value
  isOpen.value = false
}
</script>

<template>
  <v-rich-modal
    class="skipped-lessons-modal-container"
    v-model="isOpen"
    :title="modalTitle"
    @update:model-value="dismiss"
  >
    <div v-if="skippedLesson?.videoteacher_unlocked" class="skipped-lessons-modal">
      <p class="skipped-lessons-modal__text">Видео-уроки помогут быстро повторить материал.</p>
      <img class="skipped-lessons-modal__cover-img" :src="videoTeacherGame?.cover_image" />

      <ul class="skipped-lessons-modal__actions">
        <li>
          <v-button mode="success large block" @click="openVideoTeacher">
            Открыть {{ !isMobile && 'видео-учитель' }}
          </v-button>
        </li>
        <li class="flex-spacer">
          <v-button mode="primary outlined large block" @click="dismiss"> Напомнить позже </v-button>
        </li>
      </ul>
    </div>
    <div v-else-if="videoTeacherGame" class="skipped-lessons-modal">
      <p class="skipped-lessons-modal__text">
        Видео-уроки от Елены Приказновой помогают понять и усвоить материал текущего урока.
      </p>
      <img class="skipped-lessons-modal__cover-img" :src="videoTeacherGame.cover_image" />

      <ul class="skipped-lessons-modal__actions">
        <li>
          <v-button mode="primary block" @click="openVideoTeacher"> Купить </v-button>
        </li>
        <li>
          <v-button mode="primary outlined block" @click="dismiss"> Напомнить позже </v-button>
        </li>
        <li class="flex-spacer">
          <v-link mode="primary outlined block" @click="dismissAndRemember"> Больше не показывать </v-link>
        </li>
      </ul>
    </div>
  </v-rich-modal>
</template>

<style lang="scss" scoped>
@use '~/styles/pages/student2/screen' as s2;

@include s2.mobile {
  .skipped-lessons-modal-container {
    :deep(.v-rich-modal__content) {
      padding: 16px;
    }

    :deep(.v-rich-modal__container) {
      width: auto;
      height: auto;
      border-radius: 16px;
      max-height: 95%;
    }

    :deep(.v-rich-modal__header) {
      font-size: 20px;
      padding: 0 16px;
      min-height: 40px;
    }
  }
}

.skipped-lessons-modal {
  max-width: 450px;

  &__text {
    @include s2.mobile {
      @include text-caption;
    }
  }

  &__cover-img {
    height: 160px;
    margin: 24px auto;
    display: block;

    @include s2.mobile {
      height: 120px;
      margin: 8px auto;
    }
  }

  &__actions {
    display: flex;
    gap: 16px;
    flex-direction: column;

    @include s2.mobile {
      gap: 8px;
      flex-direction: row;
      flex-wrap: wrap;

      & > *:not(:last-child) {
        flex: 49%;
      }
    }
  }
}
</style>
